// SPDX-License-Identifier: AGPL-3.0-or-later

import {SubmitThemeModal} from '@app/features/discovery/components/modals/SubmitThemeModal';
import ThemeDiscoveryState, {type ThemeCategory} from '@app/features/discovery/state/ThemeDiscoveryState';
import {ThemeDiscoveryCard} from '@app/features/discovery/themes/ThemeDiscoveryCard';
import styles from '@app/features/discovery/themes/ThemeDiscoveryPage.module.css';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	FireIcon,
	LightningIcon,
	MagnifyingGlassIcon,
	MoonIcon,
	PaintBrushIcon,
	PaletteIcon,
	PlusCircleIcon,
	SparkleIcon,
	SunIcon,
	XIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

const THEME_CATEGORIES: Array<{id: ThemeCategory; label: string; icon: React.FC<{size?: number; weight?: any}>}> = [
	{id: 'all', label: 'All Themes', icon: SparkleIcon},
	{id: 'dark', label: 'Dark & OLED', icon: MoonIcon},
	{id: 'neon', label: 'Neon & Glow', icon: LightningIcon},
	{id: 'minimal', label: 'Clean Minimal', icon: SunIcon},
	{id: 'retro', label: 'Retro Synthwave', icon: FireIcon},
	{id: 'colorful', label: 'Colorful & Pastel', icon: PaletteIcon},
];

export const ThemeDiscoveryPage: React.FC = observer(() => {
	const themes = ThemeDiscoveryState.filteredThemes;
	const selectedCategory = ThemeDiscoveryState.selectedCategory;
	const searchQuery = ThemeDiscoveryState.searchQuery;

	const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		ThemeDiscoveryState.setSearchQuery(e.target.value);
	}, []);

	const handleClearSearch = useCallback(() => {
		ThemeDiscoveryState.setSearchQuery('');
	}, []);

	const handleCategoryClick = useCallback((cat: ThemeCategory) => {
		ThemeDiscoveryState.setSelectedCategory(cat);
	}, []);

	const handleOpenSubmit = useCallback(() => {
		ModalCommands.pushWithKey(modal(() => <SubmitThemeModal />), 'submit-theme-modal');
	}, []);

	return (
		<div className={styles.container} data-flx="discovery.themes.page">
			<div className={styles.hero}>
				<h1 className={styles.heroTitle}>Discover Community Themes</h1>
				<p className={styles.heroSubtitle}>
					Personalize your Nexoria experience. Browse, preview, and apply curated OLED palettes, neon glows, and community-crafted styles with a single click.
				</p>

				<div className={styles.searchBarWrapper}>
					<MagnifyingGlassIcon size={20} className={styles.searchIcon} />
					<input
						type="text"
						value={searchQuery}
						onChange={handleSearchChange}
						placeholder="Search themes by name, author, palette, or tag..."
						className={styles.searchInput}
						data-flx="discovery.themes.search-input"
					/>
					{searchQuery && (
						<button
							type="button"
							className={styles.clearButton}
							onClick={handleClearSearch}
							aria-label="Clear search"
						>
							<XIcon size={16} />
						</button>
					)}
				</div>
			</div>

			<div className={styles.toolbarRow}>
				<div className={styles.categoryBar} role="tablist">
					{THEME_CATEGORIES.map((cat) => {
						const IconComp = cat.icon;
						const isActive = selectedCategory === cat.id;
						return (
							<button
								key={cat.id}
								type="button"
								role="tab"
								aria-selected={isActive}
								className={`${styles.categoryPill} ${isActive ? styles.categoryPillActive : ''}`}
								onClick={() => handleCategoryClick(cat.id)}
								data-flx={`discovery.themes.category.${cat.id}`}
							>
								<IconComp size={16} weight={isActive ? 'fill' : 'regular'} />
								<span>{cat.label}</span>
							</button>
						);
					})}
				</div>

				<div className={styles.actionsGroup}>
					<button
						type="button"
						className={styles.submitThemeBtn}
						onClick={handleOpenSubmit}
						data-flx="discovery.themes.submit-btn"
					>
						<PlusCircleIcon size={16} weight="bold" />
						<span>Submit Theme</span>
					</button>

					<a
						href="/theme-studio"
						className={styles.studioLinkBtn}
						data-flx="discovery.themes.studio-link"
					>
						<PaintBrushIcon size={16} />
						<span>Theme Studio</span>
					</a>
				</div>
			</div>

			{ThemeDiscoveryState.pendingThemes.length > 0 && (
				<div
					style={{
						background: 'rgba(168, 85, 247, 0.1)',
						border: '1px solid rgba(168, 85, 247, 0.25)',
						borderRadius: '10px',
						padding: '12px 18px',
						marginBottom: '20px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '12px',
						flexWrap: 'wrap',
					}}
				>
					<div>
						<strong style={{color: '#f2f3f5'}}>
							{ThemeDiscoveryState.pendingThemes.length} Theme(s) Pending Review
						</strong>
						<p style={{margin: '2px 0 0 0', fontSize: '0.85rem', color: '#949ba4'}}>
							Submitted themes must be approved before public listing.
						</p>
					</div>
					<div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
						{ThemeDiscoveryState.pendingThemes.map((pendingTheme) => (
							<button
								key={pendingTheme.id}
								type="button"
								onClick={() => {
									ThemeDiscoveryState.approveTheme(pendingTheme.id);
									ToastCommands.createToast({
										type: 'success',
										children: `Theme "${pendingTheme.name}" approved and published!`,
									});
								}}
								style={{
									background: '#10b981',
									color: '#ffffff',
									border: 'none',
									borderRadius: '6px',
									padding: '6px 12px',
									fontSize: '0.82rem',
									fontWeight: 600,
									cursor: 'pointer',
								}}
							>
								Approve "{pendingTheme.name}" (Admin Test)
							</button>
						))}
					</div>
				</div>
			)}

			{themes.length === 0 ? (
				<div className={styles.emptyState}>
					<PaletteIcon size={56} weight="duotone" color="#a855f7" />
					<h3 className={styles.emptyTitle}>
						{ThemeDiscoveryState.allThemes.length === 0 ? 'No community themes listed yet' : 'No themes found'}
					</h3>
					<p style={{maxWidth: '460px', margin: '8px auto 20px auto', lineHeight: 1.5}}>
						{ThemeDiscoveryState.allThemes.length === 0
							? 'The Theme Store is open for creator submissions! Design a custom palette or export from Theme Studio, then submit it for review to be featured here.'
							: 'Try searching for a different keyword or selecting another category.'}
					</p>
					{ThemeDiscoveryState.allThemes.length === 0 && (
						<button
							type="button"
							className={styles.submitThemeBtn}
							onClick={handleOpenSubmit}
							style={{margin: '0 auto'}}
						>
							<PlusCircleIcon size={18} weight="bold" />
							<span>Submit a Theme</span>
						</button>
					)}
				</div>
			) : (
				<div className={styles.grid}>
					{themes.map((theme) => (
						<ThemeDiscoveryCard key={theme.id} theme={theme} />
					))}
				</div>
			)}
		</div>
	);
});
